import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { AxialRotationEnum } from '@ortho-next/nextray-core';
import { BaseComponent, HighlightCellDirective } from '../../shared';


/**
* Table of apex measures.
*/
@Component({
	selector: 'apex-table',
	templateUrl: './apex-table.component.html',
	imports: [
		FormsModule,
		NgbCollapseModule,
		ReactiveFormsModule,
		HighlightCellDirective
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class ApexTableComponent extends BaseComponent implements OnInit {

	isCollapsed = true;

	axialRotForm?: FormGroup;
	axialRotEnum = AxialRotationEnum;

	private readonly twoDigitsPattern: RegExp = /^\d$|^[1-9]\d{0,2}$/;


	ngOnInit(): void {
		this.initAxialRotForm();
	}

	// APEX TABLE

	/**
	* Get apex measure of femur in AP image.
	*/
	get apFemurMeasure(): string {
		return this.defAPMeasures?.def_ap_apexFemur || this.defAPMeasures?.def_ap_apexFemur === 0 ? `${this.defAPMeasures.def_ap_apexFemur} ${this.apexSignLabel(this.defAPMeasures.def_ap_apexFemurSign)}` : '';
	}

	/**
	* Get apex measure of tibia in AP image.
	*/
	get apTibiaMeasure(): string {
		return this.defAPMeasures?.def_ap_apexTibia || this.defAPMeasures?.def_ap_apexTibia === 0 ? `${this.defAPMeasures.def_ap_apexTibia} ${this.apexSignLabel(this.defAPMeasures.def_ap_apexTibiaSign)}` : '';
	}

	/**
	* Get apex measure of tibia in Lateral image.
	*/
	get ltTibiaMeasure(): string {
		return this.defLTMeasures?.def_lt_apexTibia || this.defLTMeasures?.def_lt_apexTibia === 0 ? `${this.defLTMeasures.def_lt_apexTibia} ${this.apexSignLabel(this.defLTMeasures.def_lt_apexTibiaSign)}` : '';
	}

	// AXIAL ROTATION

	private initAxialRotForm(): void {
		const validators: ValidatorFn[] = [Validators.pattern(this.twoDigitsPattern), Validators.min(0), Validators.max(99)];
		this.axialRotForm = new FormGroup({
			'axialRotFemur': new FormControl(this.def_femur_axialRotation, validators),
			'axialRotSignFemur': new FormControl(this.def_femur_axialRotationSign),
			'axialRotTibia': new FormControl(this.def_tibia_axialRotation, validators),
			'axialRotSignTibia': new FormControl(this.def_tibia_axialRotationSign)
		});
	}

	/**
	* Update axial rotation sign of femur.
	*/
	updateAxialRotSignFemur(sign: AxialRotationEnum): void {
		if (this.stateType === this.stateTypeEnum.deformityAnalysis) {
			this.canvasSrv.dispatch('setFemurAxialRotationSign', sign);
		}
	}

	/**
	* Update axial rotation value of femur.
	*/
	updateAxialRotFemur(): void {
		if (this.stateType === this.stateTypeEnum.deformityAnalysis) {
			if (this.axialRotForm?.controls['axialRotFemur'].valid) {
				const axialRotFemur = this.axialRotForm?.value.axialRotFemur ? Number.parseInt(this.axialRotForm?.value.axialRotFemur) : 0;
				this.canvasSrv.dispatch('setFemurAxialRotation', axialRotFemur);
			}
			this.axialRotForm?.controls['axialRotFemur'].setValue(this.def_femur_axialRotation);
		}
	}

	/**
	* Update axial rotation sign of tibia.
	*/
	updateAxialRotSignTibia(sign: AxialRotationEnum): void {
		if (this.stateType === this.stateTypeEnum.deformityAnalysis) {
			this.canvasSrv.dispatch('setTibiaAxialRotationSign', sign);
		}
	}

	/**
	* Update axial rotation value of tibia.
	*/
	updateAxialRotTibia(): void {
		if (this.stateType === this.stateTypeEnum.deformityAnalysis) {
			if (this.axialRotForm?.controls['axialRotTibia'].valid) {
				const axialRotTibia = this.axialRotForm?.value.axialRotTibia ? Number.parseInt(this.axialRotForm?.value.axialRotTibia) : 0;
				this.canvasSrv.dispatch('setTibiaAxialRotation', axialRotTibia);
			}
			this.axialRotForm?.controls['axialRotTibia'].setValue(this.def_tibia_axialRotation);
		}
	}

}
